import { ExpandablePanel } from '@entur/expand';

import { Modal } from '@entur/modal';
import { Table, TableBody, TableHead, TableRow, HeaderCell, DataCell } from '@entur/table';
import { Paragraph } from '@entur/typography';
import { useIntl } from 'react-intl';
import styles from './styles.module.scss';
import { useEditFormShortcuts, useSearchPageShortcuts } from './shortcutRoutes';
import { ShortcutEntry, ShortcutGroup } from './types';
import { Kbd, Plus } from 'components/Kbd';

interface KeyboardNavigationModalProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export const KeyboardNavigationModal = ({ isOpen, onDismiss }: KeyboardNavigationModalProps) => {
  const intl = useIntl();
  const isEditFormContext = useEditFormShortcuts();
  const isSearchPageContext = useSearchPageShortcuts();
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const commandAltText = isMac ? 'Cmd' : 'Ctrl';
  const commandAltKey = <Kbd>{commandAltText}</Kbd>;

  const globalShortcuts: ShortcutEntry[] = [
    {
      key: 'tab',
      combo: <Kbd>Tab</Kbd>,
      effect: intl.formatMessage({ id: 'keyboardNavigation.tab' }),
    },
    {
      key: 'shiftTab',
      combo: (
        <>
          <Kbd>Shift</Kbd>
          <Plus />
          <Kbd>Tab</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.shiftTab' }),
    },
    {
      key: 'shiftClick',
      combo: (
        <>
          <Kbd>Shift</Kbd>
          <Plus />
          <Kbd>Klikk</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.shiftClick' }),
    },
    {
      key: 'space',
      combo: <Kbd>Space</Kbd>,
      effect: (
        <ol className={styles.margin}>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.space1' })}</li>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.space2' })}</li>
        </ol>
      ),
    },
    {
      key: 'upDown',
      combo: (
        <>
          <Kbd>↑</Kbd>
          <span>{intl.formatMessage({ id: 'keyboardNavigation.or' })}</span>
          <Kbd>↓</Kbd>
        </>
      ),
      effect: (
        <ol className={styles.margin}>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.upDown1' })}</li>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.upDown2' })}</li>
        </ol>
      ),
    },
    {
      key: 'enter',
      combo: <Kbd>Enter</Kbd>,
      effect: (
        <ol className={styles.margin}>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.enter1' })}</li>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.enter2' })}</li>
        </ol>
      ),
    },
    {
      key: 'esc',
      combo: <Kbd>Esc</Kbd>,
      effect: (
        <ol className={styles.margin}>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.esc1' })}</li>
          <li>{intl.formatMessage({ id: 'keyboardNavigation.esc2' })}</li>
        </ol>
      ),
    },
    {
      key: 'leftRight',
      combo: (
        <>
          <Kbd>←</Kbd>
          <span>/</span>
          <Kbd>→</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.leftRight' }),
    },
    {
      key: 'ctrlUpDown',
      combo: (
        <>
          <Kbd>Ctrl</Kbd>
          <Plus />
          <Kbd>↓</Kbd>
          <span>/</span> <Kbd>↑</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.ctrlUpDown' }),
    },
    {
      key: 'ctrlAltT',
      combo: (
        <>
          <Kbd>Ctrl</Kbd> <Plus /> <Kbd>Alt</Kbd> <Plus /> <Kbd>T</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.ctrlAltT' }),
    },
    {
      key: 'altUpDown',
      combo: (
        <>
          <Kbd>Alt</Kbd>
          <Plus />
          <Kbd>↓</Kbd>
          <span>/</span>
          <Kbd>↑</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.altUpDown' }),
    },
    {
      key: 'altLeftRight',
      combo: (
        <>
          <Kbd>Alt</Kbd>
          <Plus />
          <Kbd>←</Kbd>
          <span>/</span>
          <Kbd>→</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.altLeftRight' }),
    },
  ];

  const editShortcuts: ShortcutEntry[] = [
    {
      key: 'modE',
      combo: (
        <>
          {commandAltKey}
          <Plus />
          <Kbd>E</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.modE' }),
    },
    {
      key: 'modS',
      combo: (
        <>
          {commandAltKey}
          <Plus />
          <Kbd>S</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.modS' }),
    },
    {
      key: 'modO',
      combo: (
        <>
          {commandAltKey}
          <Plus />
          <Kbd>O</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.modO' }),
    },
    {
      key: 'modI',
      combo: (
        <>
          {commandAltKey}
          <Plus />
          <Kbd>I</Kbd>
        </>
      ),
      effect: intl.formatMessage({ id: 'keyboardNavigation.modI' }),
    },
  ];

  const groups: ShortcutGroup[] = [
    {
      key: 'global',
      title: intl.formatMessage({ id: 'keyboardNavigation.groupGlobal' }),
      shortcuts: globalShortcuts,
    },
    {
      key: 'editForm',
      title: intl.formatMessage({ id: 'keyboardNavigation.groupEditForms' }),
      shortcuts: editShortcuts,
    },
  ];

  const activeGroupKey = isEditFormContext || isSearchPageContext ? 'editForm' : 'global';

  const renderTable = (shortcuts: ShortcutEntry[]) => (
    <Table spacing="small">
      <TableHead>
        <TableRow>
          <HeaderCell>{intl.formatMessage({ id: 'keyboardNavigation.keyboardCombos' })}</HeaderCell>
          <HeaderCell>{intl.formatMessage({ id: 'keyboardNavigation.effects' })}</HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {shortcuts.map((shortcut) => (
          <TableRow key={shortcut.key}>
            <DataCell className={styles.shortcutComboCell}>{shortcut.combo}</DataCell>
            <DataCell>{shortcut.effect}</DataCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Modal
      size="large"
      title={intl.formatMessage({ id: 'keyboardNavigation.type' })}
      open={isOpen}
      onDismiss={onDismiss}
    >
      <div className={styles.modalContent}>
        <Paragraph>{intl.formatMessage({ id: 'keyboardNavigation.keyboardDescription' })}</Paragraph>
        <div className={styles.expandableGroups}>
          {groups.map((group) => {
            const shouldOpen = activeGroupKey === group.key;

            return (
              <ExpandablePanel key={group.key} title={group.title} defaultOpen={shouldOpen}>
                {renderTable(group.shortcuts)}
              </ExpandablePanel>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
